import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient, apiErrorDetail } from '@/lib/api-client'
import toast from 'react-hot-toast'

interface User {
  id: string
  email: string
  name: string
  roles: Array<{ slug: string; name: string }>
  permissions: string[]
  mfaEnabled: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string, mfaToken?: string) => Promise<{ requiresMfa: boolean; userId?: string }>
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('user')
    const accessToken = localStorage.getItem('accessToken')
    
    if (storedUser && accessToken) {
      try {
        setUser(JSON.parse(storedUser))
        // Optionally refresh user data
        refreshUser().catch(() => {
          // If refresh fails, clear invalid session
          localStorage.removeItem('user')
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
          setUser(null)
        })
      } catch (error) {
        console.error('Invalid stored user data:', error)
        localStorage.removeItem('user')
      }
    }
    
    setLoading(false)
  }, [])

  const login = async (email: string, password: string, mfaToken?: string) => {
    try {
      const result = await apiClient.login(email, password, mfaToken)
      
      if (result.requiresMfa) {
        return { requiresMfa: true, userId: result.userId }
      }
      
      setUser(result.user)
      toast.success('Successfully logged in!')
      return { requiresMfa: false }
    } catch (error: unknown) {
      const message = apiErrorDetail(error, 'Login failed')
      toast.error(message)
      throw error
    }
  }

  const register = async (email: string, password: string, name: string) => {
    try {
      const user = await apiClient.register(email, password, name)
      setUser(user)
      toast.success('Successfully registered!')
    } catch (error: unknown) {
      const message = apiErrorDetail(error, 'Registration failed')
      toast.error(message)
      throw error
    }
  }

  const logout = async () => {
    try {
      await apiClient.logout()
      setUser(null)
      toast.success('Successfully logged out')
    } catch (error) {
      console.error('Logout error:', error)
      // Clear local state even if API call fails
      setUser(null)
      localStorage.removeItem('user')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
    }
  }

  const refreshUser = async () => {
    try {
      const response = await apiClient.getCurrentUser()
      const userData = response.data
      setUser(userData)
      localStorage.setItem('user', JSON.stringify(userData))
    } catch (error) {
      console.error('Failed to refresh user:', error)
      throw error
    }
  }

  const hasPermission = (permission: string): boolean => {
    if (!user) return false
    return user.permissions.includes(permission)
  }

  const hasRole = (role: string): boolean => {
    if (!user) return false
    return user.roles.some(r => r.slug === role)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// The provider component and its consumer hook deliberately live in the same
// module — the canonical React context layout. That costs this file Fast
// Refresh granularity, nothing else.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}